import { type ReactNode } from 'react'
import { confirmActionModal } from '@util'
import { CompanySetupInputSchema, GLOBALTEMPLATE_ID, isNotEmpty, type CompanySetupInput } from '@terros/common'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { useForm } from '@mantine/form'
import { Grid, MultiSelect, NumberInput, Select, Stack, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  COMPANY_INDUSTRY_OPTIONS,
  companyKinds,
  useExistingCompanies,
  useSetupCompany,
  useTranslation,
  zodResolver,
} from '@front/model'
import { ButtonSave, Page, TSection } from '@components'
import { TemplateOptions } from './TemplateOptions'
import { SuggestedCompanies } from './SuggestedCompanies'

function Companies(): ReactNode {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { add, isAdding, error } = useSetupCompany()
  useToastError(error)

  const form = useForm<CompanySetupInput>({
    initialValues: {
      company: { name: '', maxUsers: 10, kind: 'customer' },
      admin: {
        firstName: '',
        lastName: '',
        email: '',
      },
      templateId: GLOBALTEMPLATE_ID,
    },
    validate: zodResolver(CompanySetupInputSchema),
  })

  const { isLoading: isLoadingExistingCompanies, companies: suggestedCompanies } = useExistingCompanies({
    name: form.values.company.name,
  })

  const handleAdd = async (values: CompanySetupInput): Promise<void> => {
    if (isNotEmpty(suggestedCompanies)) {
      const confirmed = await confirmActionModal({
        title: t('Possible Duplicate Companies'),
        message: t('There are possible duplicate companies. Are you sure you want to continue?'),
        t,
      })
      if (!confirmed) return
    }
    const { company } = await add(values)
    navigate({ to: `/companies/${company.companyId}` })
  }

  return (
    <Page>
      <form onSubmit={form.onSubmit(handleAdd)}>
        <Page.Heading back title={t('Add Company')}>
          <ButtonSave disabled={isLoadingExistingCompanies} loading={isAdding} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TSection heading={{ title: t('Company Details') }}>
                <TextInput
                  required
                  label={t('Company Name')}
                  placeholder='ACME Inc.'
                  {...form.getInputProps('company.name')}
                />
                <NumberInput label={t('Max Users')} {...form.getInputProps('company.maxUsers')} />
                <MultiSelect
                  clearable
                  data={COMPANY_INDUSTRY_OPTIONS}
                  label={t('Industries')}
                  searchable
                  {...form.getInputProps('company.industries')}
                />
                <Select
                  data={companyKinds}
                  label={t('Kind')}
                  allowDeselect={false}
                  clearable={false}
                  {...form.getInputProps('company.kind')}
                />
                <TemplateOptions form={form} />
              </TSection>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack>
                <TSection heading={{ title: t('Admin Details') }}>
                  <TextInput required label={t('First Name')} {...form.getInputProps('admin.firstName')} />
                  <TextInput required label={t('Last Name')} {...form.getInputProps('admin.lastName')} />
                  <TextInput type='email' required label={t('Email')} {...form.getInputProps('admin.email')} />
                </TSection>
                <SuggestedCompanies companies={suggestedCompanies} isLoading={isLoadingExistingCompanies} />
              </Stack>
            </Grid.Col>
          </Grid>
        </Page.Content>
      </form>
    </Page>
  )
}

export const Route = createFileRoute('/companies_/add/')({
  component: Companies,
})
